/**
 * Theme store — dark/light/system. Persisté.
 *
 * @remarks
 * Le `ThemeProvider` (cf. `app/providers/`) consomme ce store et applique
 * `data-theme="dark"` ou `data-theme="light"` au `<html>` selon la valeur.
 * Les CSS overrides dans `tokens.css` font le reste.
 *
 * `'system'` = suit la préférence OS via `prefers-color-scheme`.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

type Theme = 'light' | 'dark' | 'system';

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

export const useThemeStore = create<ThemeState>()(
  devtools(
    persist(
      (set) => ({
        theme: 'system',
        setTheme: (theme) => {
          set({ theme });
        },
      }),
      {
        name: 'saas-front-v2:theme',
        version: 1,
        partialize: (state) => ({ theme: state.theme }),
        /**
         * Migration policy : à chaque changement de shape (ex: ajout d'un
         * thème, renommage), bumper `version` et migrer. Fallback safe : si
         * payload inattendu, retourne `'system'` (le défaut).
         */
        // ⚠️ Pas de `if (oldVersion === <version courante>)` ici : zustand n'appelle
        // `migrate` que si les versions diffèrent, la branche serait morte. Au
        // prochain bump, adapter AVANT la revalidation en comparant avec `<` :
        // `if (oldVersion < 2) { …adapter… }`. Cascade de référence :
        // `use-layout-store`. (SAAS-903)
        migrate: (persistedState: unknown) => {
          try {
            // La valeur est revalidée quelle que soit sa provenance — c'est ce qui
            // permet de récupérer un thème valide d'une version inconnue.
            const candidate = (persistedState as { theme?: unknown } | null)?.theme;
            if (candidate === 'light' || candidate === 'dark' || candidate === 'system') {
              return { theme: candidate };
            }
            return { theme: 'system' };
          } catch {
            return { theme: 'system' };
          }
        },
      },
    ),
    { name: 'theme', enabled: import.meta.env.DEV },
  ),
);
